/**
 * deviceLabel.ts — Device naming heuristics and normalization.
 *
 * Generates platform-aware default device labels and enforces length & character safety
 * before transmission to the server.
 */

export const MAX_DEVICE_LABEL_LENGTH = 64

/**
 * Normalizes a device label:
 * - Strips non-printable control characters
 * - Trims leading and trailing whitespace
 * - Enforces the 64-character limit
 * - Falls back to a safe non-empty label if empty
 */
export function normalizeDeviceLabel(label: string | null | undefined): string {
  if (!label) return 'New Device'
  const cleaned = label
    .replace(/[\x00-\x1F\x7F]/g, '')
    .trim()
    .slice(0, MAX_DEVICE_LABEL_LENGTH)
  return cleaned || 'New Device'
}

/**
 * Parses userAgent to generate a human-friendly heuristic label, e.g. "Chrome on macOS".
 * Best-effort only — modern browsers increasingly genericize UA strings.
 */
export function parseUserAgentHeuristic(ua?: string): string {
  const userAgent = ua || (typeof navigator !== 'undefined' ? navigator.userAgent : '')
  if (!userAgent) return 'Web Browser'

  // Browser detection
  let browser = 'Browser'
  if (/Edg\//i.test(userAgent)) {
    browser = 'Edge'
  } else if (/OPR\/|Opera/i.test(userAgent)) {
    browser = 'Opera'
  } else if (/Chrome\/|CriOS/i.test(userAgent)) {
    browser = 'Chrome'
  } else if (/Firefox\/|FxiOS/i.test(userAgent)) {
    browser = 'Firefox'
  } else if (/Safari/i.test(userAgent) && !/Chrome/i.test(userAgent)) {
    browser = 'Safari'
  }

  // OS detection
  let os = 'Web'
  if (/iPhone/i.test(userAgent)) {
    os = 'iPhone'
  } else if (/iPad/i.test(userAgent)) {
    os = 'iPad'
  } else if (/Android/i.test(userAgent)) {
    os = 'Android'
  } else if (/Macintosh|Mac OS X/i.test(userAgent)) {
    os = 'macOS'
  } else if (/Windows NT/i.test(userAgent)) {
    os = 'Windows'
  } else if (/CrOS/i.test(userAgent)) {
    os = 'ChromeOS'
  } else if (/Linux/i.test(userAgent)) {
    os = 'Linux'
  }

  return `${browser} on ${os}`
}

/**
 * Generates a default device label:
 * 1. Tauri desktop / mobile: pulls device hostname via Tauri bridge command.
 * 2. Web: heuristic from navigator.userAgent.
 * 3. Fallback: "New Device".
 */
export async function generateDefaultDeviceLabel(): Promise<string> {
  const isTauri = typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window

  if (isTauri) {
    try {
      const { tauriApi } = await import('./tauriBridge')
      if (tauriApi && typeof tauriApi.getHostname === 'function') {
        const hostname = await tauriApi.getHostname()
        if (hostname && hostname.trim()) {
          return normalizeDeviceLabel(hostname)
        }
      }
    } catch {
      // Fall through to UA heuristic
    }
  }

  const heuristic = parseUserAgentHeuristic()
  if (heuristic && heuristic !== 'Browser on Web') {
    return normalizeDeviceLabel(heuristic)
  }

  return isTauri ? 'Desktop App' : 'New Device'
}
